import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import { useIssueDiscoverySas } from '../hooks';

interface SampleUploaderProps {
  /** Called once every queued file has been PUT to its SAS URI. */
  onSamplesSelected: (blobPaths: string[]) => void;
  /** Disable interaction while the parent mutation is in flight. */
  busy?: boolean;
}

const MAX_FILES = 10;
const MAX_BYTES = 20 * 1024 * 1024; // 20MB
const PDF_MIME = 'application/pdf';

type UploadState = 'queued' | 'uploading' | 'done' | 'error';

interface QueuedFile {
  file: File;
  name: string;
  size: number;
  state: UploadState;
  /** Set when the file lands in Blob; surfaced to the parent on completion. */
  blobPath?: string;
  /** Last error string when state === 'error'. */
  errorMessage?: string;
}

function isPdf(file: File): boolean {
  // Some browsers / OSes mislabel the MIME type; fall back to the extension.
  if (file.type === PDF_MIME) {
    return true;
  }
  return file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Drag-and-drop area for the discovery wizard's first step.
 *
 * Restrictions: ≤10 files, only PDF, ≤20MB per file. Invalid files surface an
 * inline error and never reach the parent callback.
 *
 * Deferral D4 (Phase 6): files are uploaded directly to Azure Blob Storage via
 * a SAS URI minted by `POST /discovery/sas`. The API never proxies the PDF
 * payload — only the canonical blob paths reach `POST /discovery`.
 */
export function SampleUploader({
  onSamplesSelected,
  busy = false,
}: SampleUploaderProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState<boolean>(false);
  const issueSas = useIssueDiscoverySas();

  const reset = (): void => {
    setQueue([]);
    setError(null);
    if (inputRef.current !== null) {
      inputRef.current.value = '';
    }
  };

  const updateAt = (index: number, patch: Partial<QueuedFile>): void => {
    setQueue((prev) => {
      const next = prev.slice();
      const current = next[index];
      if (current === undefined) {
        return prev;
      }
      next[index] = { ...current, ...patch };
      return next;
    });
  };

  const uploadOne = async (
    file: File,
    sasUri: string,
    blobPath: string,
    index: number,
  ): Promise<string | null> => {
    updateAt(index, { state: 'uploading' });
    try {
      const response = await fetch(sasUri, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type !== '' ? file.type : PDF_MIME,
        },
        body: file,
      });
      if (!response.ok) {
        const detail = `${response.status} ${response.statusText}`;
        updateAt(index, { state: 'error', errorMessage: detail });
        return null;
      }
      updateAt(index, { state: 'done', blobPath });
      return blobPath;
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Error desconocido al subir.';
      updateAt(index, { state: 'error', errorMessage: detail });
      return null;
    }
  };

  const ingest = async (files: FileList | File[]): Promise<void> => {
    const list = Array.from(files);
    if (list.length === 0) {
      return;
    }
    if (list.length > MAX_FILES) {
      setError(`Máximo ${MAX_FILES} archivos. Recibidos ${list.length}.`);
      return;
    }
    const oversized = list.find((f) => f.size > MAX_BYTES);
    if (oversized !== undefined) {
      setError(`"${oversized.name}" supera el límite de 20 MB.`);
      return;
    }
    const nonPdf = list.find((f) => !isPdf(f));
    if (nonPdf !== undefined) {
      setError(`"${nonPdf.name}" no es un PDF. Solo se aceptan archivos PDF.`);
      return;
    }

    setError(null);
    setQueue(
      list.map((f) => ({
        file: f,
        name: f.name,
        size: f.size,
        state: 'queued',
      })),
    );

    try {
      const sas = await issueSas.mutateAsync({ count: list.length });
      if (sas.targets.length !== list.length) {
        setError('La cantidad de SAS recibidos no coincide con los archivos.');
        return;
      }

      const results = await Promise.all(
        list.map((file, i) => {
          const target = sas.targets[i];
          if (target === undefined) {
            return Promise.resolve(null);
          }
          return uploadOne(file, target.sasUri, target.blobPath, i);
        }),
      );

      const paths = results.filter((p): p is string => p !== null);
      if (paths.length === list.length) {
        onSamplesSelected(paths);
      } else {
        setError('Algunas subidas fallaron. Usa "Reintentar" en cada archivo.');
      }
    } catch (err) {
      const detail =
        err instanceof Error ? err.message : 'No se pudieron obtener los SAS de subida.';
      setError(detail);
    }
  };

  const retryOne = async (index: number): Promise<void> => {
    const current = queue[index];
    if (current === undefined) {
      return;
    }
    try {
      const sas = await issueSas.mutateAsync({ count: 1 });
      const target = sas.targets[0];
      if (target === undefined) {
        updateAt(index, { state: 'error', errorMessage: 'SAS vacío.' });
        return;
      }
      const path = await uploadOne(current.file, target.sasUri, target.blobPath, index);
      if (path !== null) {
        // If every file is now done, forward the consolidated path list to the
        // parent so the wizard can advance without the user re-dropping files.
        setQueue((prev) => {
          const allDone = prev.every((f) => f.state === 'done');
          if (allDone) {
            onSamplesSelected(prev.map((f) => f.blobPath ?? '').filter((p) => p !== ''));
          }
          return prev;
        });
      }
    } catch (err) {
      const detail =
        err instanceof Error ? err.message : 'No se pudo reintentar la subida.';
      updateAt(index, { state: 'error', errorMessage: detail });
    }
  };

  const onFileInput = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files !== null) {
      void ingest(event.target.files);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length > 0) {
      void ingest(event.dataTransfer.files);
    }
  };

  const anyUploading = queue.some((f) => f.state === 'uploading') || issueSas.isPending;

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        aria-label="Soltar archivos PDF aquí"
        aria-disabled={busy || anyUploading}
        onClick={() => {
          if (!busy && !anyUploading) {
            inputRef.current?.click();
          }
        }}
        onKeyDown={(event) => {
          if ((event.key === 'Enter' || event.key === ' ') && !busy && !anyUploading) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!busy && !anyUploading) {
            setDragging(true);
          }
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition ' +
          (dragging
            ? 'border-primary bg-primary/5'
            : 'border-slate-300 bg-slate-50 hover:bg-slate-100') +
          (busy || anyUploading ? ' pointer-events-none opacity-60' : '')
        }
      >
        <p className="text-base font-semibold text-slate-900">
          Arrastra 1 PDF y te propongo el esquema
        </p>
        <p className="mt-1 text-sm text-slate-500">
          o haz clic para seleccionarlo. Puedes añadir más (hasta 10) para reforzar la propuesta. Máximo 20 MB por archivo.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={onFileInput}
          disabled={busy || anyUploading}
        />
      </div>

      {error !== null ? (
        <div
          role="alert"
          className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          {error}
        </div>
      ) : null}

      {queue.length > 0 ? (
        <div className="rounded-md border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-100 text-sm">
            {queue.map((file, index) => (
              <li
                key={`${file.name}-${file.size}-${index}`}
                className="flex flex-col gap-1 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate text-slate-700">{file.name}</span>
                  <span className="text-xs tabular-nums text-slate-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    role="progressbar"
                    aria-valuenow={
                      file.state === 'done' ? 100 : file.state === 'uploading' ? 50 : 0
                    }
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Progreso de ${file.name}`}
                    className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100"
                  >
                    <div
                      className={
                        'h-full transition-all ' +
                        (file.state === 'done'
                          ? 'w-full bg-emerald-500'
                          : file.state === 'uploading'
                            ? 'w-1/2 bg-primary animate-pulse'
                            : file.state === 'error'
                              ? 'w-full bg-rose-500'
                              : 'w-0 bg-slate-300')
                      }
                    />
                  </div>
                  <span
                    className={
                      'text-xs ' +
                      (file.state === 'done'
                        ? 'text-emerald-700'
                        : file.state === 'error'
                          ? 'text-rose-700'
                          : 'text-slate-500')
                    }
                  >
                    {file.state === 'done'
                      ? 'Subido'
                      : file.state === 'uploading'
                        ? 'Subiendo…'
                        : file.state === 'error'
                          ? 'Error'
                          : 'En cola'}
                  </span>
                  {file.state === 'error' ? (
                    <button
                      type="button"
                      className="btn-secondary py-0.5 text-xs"
                      onClick={() => void retryOne(index)}
                    >
                      Reintentar
                    </button>
                  ) : null}
                </div>
                {file.state === 'error' && file.errorMessage !== undefined ? (
                  <p className="text-xs text-rose-700">{file.errorMessage}</p>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
            <span>{queue.length} archivo(s) en cola.</span>
            <button
              type="button"
              className="btn-secondary py-1 text-xs"
              onClick={reset}
              disabled={anyUploading}
            >
              Vaciar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
